/**
 * Teaching Agent API Service
 *
 * Client-side integration with the AI Teaching Agent backend.
 * Provides streaming chat, session management, and content APIs.
 */

import { consumeTutorStream, isAbortError } from './sse/consumeTutorStream';
import type { TutorPhase, TutorStreamEvent, TutorStreamSource } from './sse/streamEvents';
import { normalizeRailAction, MAX_RAIL_ACTIONS_PER_TURN } from './sse/railAction';
import type { RailAction } from './sse/railAction';
import { useAuthStore } from '../store/authStore';

/**
 * Base URLs are split by concern:
 *  - CHAT (sessions + chat lifecycle) → the OpenAI tutor-agent when
 *    REACT_APP_TUTOR_URL is set, else the shared teaching-agent.
 *  - CONTENT (videos/transcript/captions/progress/books/search/recommend) →
 *    always the teaching-agent, which owns the media + curriculum surface.
 */
const TEACHING_AGENT_URL = process.env.REACT_APP_TEACHING_AGENT_URL || '/api/teaching';
/**
 * Chat base resolution. REACT_APP_AKO_BFF=true (default OFF) routes sessions + chat
 * through the api-proxy `/ako` BFF, which picks teaching-agent or tutor-agent
 * server-side (TUTOR_RUNTIME, canary). Unset => exactly the previous behaviour.
 * See docs/ai-tutor/runtime-routing.md.
 */
export function resolveChatBaseUrl(env: Record<string, string | undefined>): string {
  if (env.REACT_APP_AKO_BFF === 'true') {
    return `${(env.REACT_APP_API_PROXY_URL || 'http://localhost:3004').replace(/\/+$/, '')}/ako`;
  }
  return env.REACT_APP_TUTOR_URL || env.REACT_APP_TEACHING_AGENT_URL || '/api/teaching';
}
const CHAT_BASE_URL = resolveChatBaseUrl(process.env);
/** Learner memory lives behind the /ako BFF only (the browser cannot reach tutor-agent). */
const MEMORY_ENABLED = process.env.REACT_APP_AKO_BFF === 'true';

/** Max length of a note the learner can add; the BFF enforces the same cap. */
export const MEMORY_NOTE_MAX = 200;

export interface MemoryItem {
  id: number;
  kind: string;
  topic?: string | null;
  note: string;
  updatedAt?: string;
}

/** Why a memory call failed: `unavailable` means hide the control; `rejected` means the note was refused. */
export class MemoryError extends Error {
  constructor(public readonly code: 'unavailable' | 'rejected' | 'failed') {
    super(`memory_${code}`);
  }
}
const CONTENT_BASE_URL = TEACHING_AGENT_URL;

/**
 * Legacy callers use ApiBridge's active-token mirror, never an arbitrary
 * cached issuer/client. Canonical tutors instead bind an explicit OIDC token.
 */
function getAccessToken(): string | null {
  return useAuthStore.getState().token;
}

/** A timed transcript segment (click-to-seek + highlight). */
export interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
}

/** Toggleable WebVTT caption track. */
export interface CaptionTrack {
  lang: string;
  vttUrl: string;
  source: string;
  isDefault: boolean;
}

/** A user's per-video watch progress (resume + coverage). */
export interface VideoProgress {
  coveragePct: number;
  completed: boolean;
  lastPositionSec: number;
  durationSec?: number | null;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  metadata?: MessageMetadata;
}

/** One Pulse concept: opaque id + short display name. */
export interface PulseConcept {
  qid: string;
  name: string;
}

/**
 * What Pulse knows about the CURRENT LESSON (curriculum graph) and, when it has data, the learner.
 * Arrives as a tutor-agent `pulse` frame before `end`. Staff-only in the UI for now.
 */
export interface PulseLens {
  scope: 'lesson';
  lesson?: string;
  concepts: PulseConcept[];
  prerequisites: PulseConcept[];
  next: PulseConcept[];
  learner?: { tracked?: number; dueReviews?: number; goal?: string };
  /** Pulse answered and holds no validated concept for this lesson yet. */
  unmapped?: boolean;
}

const asConcepts = (v: unknown): PulseConcept[] =>
  Array.isArray(v)
    ? v
        .filter((c): c is Record<string, unknown> => !!c && typeof c === 'object')
        .map((c) => ({ qid: String(c.qid ?? '').slice(0, 80), name: String(c.name ?? c.qid ?? '').slice(0, 80) }))
        .filter((c) => c.qid && c.name)
        .slice(0, 5)
    : [];

/** Defensive parse of a `pulse` frame; null when there is nothing worth showing. */
export function toPulseLens(data: unknown): PulseLens | null {
  if (!data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  const concepts = asConcepts(d.concepts);
  const prerequisites = asConcepts(d.prerequisites);
  const next = asConcepts(d.next);
  let learner: PulseLens['learner'];
  if (d.learner && typeof d.learner === 'object') {
    const l = d.learner as Record<string, unknown>;
    const count = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? Math.floor(x) : undefined);
    const tracked = count(l.tracked);
    const dueReviews = count(l.dueReviews);
    const goal = typeof l.goal === 'string' && l.goal.trim() ? l.goal.slice(0, 120) : undefined;
    if (tracked !== undefined || dueReviews !== undefined || goal) {
      learner = {
        ...(tracked !== undefined ? { tracked } : {}),
        ...(dueReviews !== undefined ? { dueReviews } : {}),
        ...(goal ? { goal } : {}),
      };
    }
  }
  const unmapped = d.unmapped === true;
  if (!concepts.length && !prerequisites.length && !next.length && !learner && !unmapped) return null;
  const lesson = typeof d.lesson === 'string' && d.lesson.trim() ? d.lesson.slice(0, 120) : undefined;
  return { scope: 'lesson', ...(lesson ? { lesson } : {}), concepts, prerequisites, next, ...(learner ? { learner } : {}), ...(unmapped ? { unmapped: true } : {}) };
}

export interface MessageMetadata {
  /** Pulse lens for this reply (staff-only UI). */
  pulse?: PulseLens;
  toolCalls?: ToolCall[];
  thinking?: boolean;
  contentType?: 'text' | 'code' | 'video' | 'book' | 'question' | 'celebration';
  /** Grounding sources, rendered as chips when the backend supplies them. */
  sources?: TutorSourceRef[];
  /**
   * Server-assigned stable message id (also copied onto ChatMessage.id once
   * known). Presence means feedback can be submitted for this message.
   */
  messageId?: string;
  /** True when the learner pressed Stop; `content` is the partial reply. */
  stopped?: boolean;
}

export interface TutorSourceRef {
  id?: string;
  title: string;
  url?: string;
  kind?: string;
}

/** Final/done SSE event metadata: {messageId, sources?}. */
export interface StreamDoneMeta {
  messageId?: string;
  sources?: TutorSourceRef[];
}

/** Defensive parse of the done/final event payload; tolerates absence. */
export function parseDoneMeta(data: unknown): StreamDoneMeta {
  if (!data || typeof data !== 'object') return {};
  const d = data as Record<string, unknown>;
  const raw = (d.metadata && typeof d.metadata === 'object' ? d.metadata : d) as Record<string, unknown>;
  const out: StreamDoneMeta = {};
  if (typeof raw.messageId === 'string' && raw.messageId.trim()) out.messageId = raw.messageId;
  if (Array.isArray(raw.sources)) {
    const sources = raw.sources
      .filter((s): s is Record<string, unknown> => !!s && typeof s === 'object' && typeof (s as Record<string, unknown>).title === 'string')
      .map((s) => ({
        title: s.title as string,
        ...(typeof s.id === 'string' ? { id: s.id } : {}),
        ...(typeof s.url === 'string' ? { url: s.url } : {}),
        ...(typeof s.kind === 'string' ? { kind: s.kind } : {}),
      }));
    if (sources.length) out.sources = sources;
  }
  return out;
}

export interface ToolCall {
  name: string;
  input: Record<string, unknown>;
  status: 'pending' | 'running' | 'completed' | 'failed';
}

export interface StreamEvent {
  type: string;
  data: unknown;
  timestamp: string;
}

export interface VideoContent {
  videoId: string;
  title: string;
  url: string;
  reason: string;
  startTime?: number;
}

export interface BookContent {
  bookId: string;
  chapter: string;
  excerpt: string;
  citation: string;
  pageReference?: string;
}

export interface SocraticQuestion {
  question: string;
  type: 'clarifying' | 'probing' | 'connecting' | 'challenging';
  relatedConcept?: string;
}

export interface Celebration {
  achievement: string;
  encouragement: string;
  nextSuggestion?: string;
}

export interface SessionInfo {
  sessionId: string;
  studentId?: string;
  labId?: string | null;
  courseId?: string | null;
  courseItemId?: string | null;
  createdAt: Date;
  messageCount: number;
  status?: 'active' | 'ended' | 'abandoned';
  contextSummary?: string | null;
}

export interface ContentRecommendation {
  type: 'video' | 'book' | 'lab';
  id: string;
  title: string;
  reason: string;
  relevanceScore: number;
}

// Event callbacks for streaming
export interface StreamCallbacks {
  onStart?: () => void;
  onContent?: (text: string) => void;
  onToolCall?: (tool: ToolCall) => void;
  onVideo?: (video: VideoContent) => void;
  onBook?: (book: BookContent) => void;
  onQuestion?: (question: SocraticQuestion) => void;
  onCelebration?: (celebration: Celebration) => void;
  /** tutor-agent `pulse` frame: what Pulse knows about this lesson (and the learner, if it has data). */
  onPulse?: (pulse: PulseLens) => void;
  /** tutor-agent `rail_action` (validated reference, max 4 per turn). Absent handler = ignored. */
  onRailAction?: (action: RailAction) => void;
  onEnd?: (info?: {
    messageId?: string;
    sources?: TutorStreamSource[];
    stopReason?: string;
    /** Server reported the turn as Stopped (tutor-agent only). */
    stopped?: boolean;
    turnId?: string;
  }) => void;
  onError?: (error: Error) => void;
  /** Phase changes: thinking -> tool -> writing (see docs/ai-tutor/streaming-events.md). */
  onStatus?: (status: { phase: TutorPhase; tool?: string; label?: string }) => void;
  /** Short, learner-safe step summary, e.g. "Checking the lesson transcript". */
  onReasoning?: (text: string) => void;
  onSources?: (sources: TutorStreamSource[]) => void;
  /** Heartbeats and any bytes: lets the UI distinguish "slow" from "dead". */
  onActivity?: () => void;
  /** The caller aborted the request (Stop). Resolves instead of throwing. */
  onAbort?: () => void;
}

/**
 * Teaching Agent API Client
 */
export class TeachingAgentAPI {
  /** Base for CHAT: /sessions* and /chat* (OpenAI tutor-agent by default). */
  private chatBaseUrl: string;
  /** Base for CONTENT: /content/* (teaching-agent — media + curriculum). */
  private contentBaseUrl: string;
  private memoryEnabled: boolean;
  private accessToken: string | null | undefined;

  constructor(
    chatBaseUrl: string = CHAT_BASE_URL,
    contentBaseUrl: string = CONTENT_BASE_URL,
    memoryEnabled: boolean = MEMORY_ENABLED,
    accessToken?: string | null,
  ) {
    this.memoryEnabled = memoryEnabled;
    this.chatBaseUrl = chatBaseUrl;
    this.contentBaseUrl = contentBaseUrl;
    this.accessToken = accessToken;
  }

  /** Capture one verified auth snapshot for every call in a tutor lifecycle.
   * Explicit null fails closed and never consults any cache or token mirror. */
  withAccessToken(accessToken: string | null): TeachingAgentAPI {
    return new TeachingAgentAPI(this.chatBaseUrl, this.contentBaseUrl, this.memoryEnabled, accessToken);
  }

  private authHeaders(extra?: Record<string, string>): Record<string, string> {
    const token = this.accessToken === undefined ? getAccessToken() : this.accessToken;
    if (this.accessToken !== undefined && !token) throw new Error('Authentication is required for the teaching session.');
    return { ...(extra ?? {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  }

  /**
   * Create a new teaching session
   */
  async createSession(options: {
    labId?: string;
    courseId?: string;
    courseItemId?: string;
    initialContext?: Record<string, unknown>;
    persistenceContext?: string;
  }): Promise<SessionInfo> {
    const response = await fetch(`${this.chatBaseUrl}/sessions`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      throw new Error(`Failed to create session: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      ...data,
      createdAt: new Date(data.createdAt ?? data.startedAt),
    };
  }

  /**
   * Get session info
   */
  async getSession(sessionId: string): Promise<SessionInfo | null> {
    const response = await fetch(`${this.chatBaseUrl}/sessions/${sessionId}`, {
      headers: this.authHeaders(),
    });

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(`Failed to get session: ${response.statusText}`);
    }

    const data = await response.json();
    return {
      ...data,
      createdAt: new Date(data.createdAt ?? data.startedAt),
    };
  }

  /**
   * Send a chat message (non-streaming)
   */
  async sendMessage(options: {
    sessionId: string;
    message: string;
    labContext?: {
      labId: string;
      labTitle: string;
      instructions: string;
      objectives: string[];
    };
  }): Promise<{ response: string; events: StreamEvent[] }> {
    const response = await fetch(`${this.chatBaseUrl}/chat`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      throw new Error(`Failed to send message: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Send a chat message with streaming response
   */
  async sendMessageStreaming(
    options: {
      sessionId: string;
      message: string;
      labContext?: {
        labId: string;
        labTitle: string;
        instructions: string;
        objectives: string[];
      };
    },
    callbacks: StreamCallbacks,
    signal?: AbortSignal
  ): Promise<void> {
    let response: Response;
    try {
      response = await fetch(`${this.chatBaseUrl}/chat/stream`, {
        method: 'POST',
        headers: this.authHeaders({
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
        }),
        body: JSON.stringify(options),
        ...(signal ? { signal } : {}),
      });
    } catch (error) {
      if (isAbortError(error)) {
        callbacks.onAbort?.();
        return;
      }
      throw error;
    }

    if (!response.ok) {
      throw new Error(`Failed to start stream: ${response.statusText}`);
    }

    try {
      let railActionsSeen = 0;
      await consumeTutorStream(response, {
        signal,
        onActivity: callbacks.onActivity,
        onEvent: (event) => {
          if (event.kind === 'passthrough' && event.type === 'rail_action') {
            const action = railActionsSeen < MAX_RAIL_ACTIONS_PER_TURN ? normalizeRailAction(event.data) : null;
            if (action) {
              railActionsSeen += 1;
              callbacks.onRailAction?.(action);
            }
            return;
          }
          this.handleStreamEvent(event, callbacks);
        },
      });
    } catch (error) {
      if (isAbortError(error)) {
        callbacks.onAbort?.();
        return;
      }
      throw error;
    }
  }

  /**
   * Dispatch one normalised stream event to the callbacks.
   */
  private handleStreamEvent(event: TutorStreamEvent, callbacks: StreamCallbacks): void {
    switch (event.kind) {
      case 'start':
        callbacks.onStart?.();
        break;
      case 'status':
        callbacks.onStatus?.({ phase: event.phase, tool: event.tool, label: event.label });
        break;
      case 'reasoning':
        callbacks.onReasoning?.(event.text);
        break;
      case 'delta':
        callbacks.onContent?.(event.text);
        break;
      case 'sources':
        callbacks.onSources?.(event.sources);
        break;
      case 'done':
        callbacks.onEnd?.({
          messageId: event.messageId,
          sources: event.sources,
          stopReason: event.stopReason,
          ...(event.stopped ? { stopped: true } : {}),
          ...(event.turnId ? { turnId: event.turnId } : {}),
        });
        break;
      case 'error':
        callbacks.onError?.(new Error(event.code ? `${event.code}: ${event.message}` : event.message));
        break;
      case 'tool':
        break;
      case 'passthrough':
        this.handleRichEvent(event.type, event.data, callbacks);
        break;
    }
  }

  private handleRichEvent(type: string, data: unknown, callbacks: StreamCallbacks): void {
    switch (type) {
      case 'tool_call':
        callbacks.onToolCall?.(data as ToolCall);
        break;
      case 'video':
        callbacks.onVideo?.(data as VideoContent);
        break;
      case 'book_excerpt':
        callbacks.onBook?.(data as BookContent);
        break;
      case 'question':
        callbacks.onQuestion?.(data as SocraticQuestion);
        break;
      case 'celebration':
        callbacks.onCelebration?.(data as Celebration);
        break;
      case 'pulse': {
        const pulse = toPulseLens(data);
        if (pulse) callbacks.onPulse?.(pulse);
        break;
      }
    }
  }

  /**
   * Interrupt current response
   */
  async interrupt(sessionId: string): Promise<void> {
    await fetch(`${this.chatBaseUrl}/chat/interrupt`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ sessionId }),
    });
  }

  /**
   * List the learner's active sessions (newest activity first). The server
   * derives identity from the token; the path segment is informational only.
   * Used to recover the conversation for a course item in a fresh tab.
   */
  async listActiveSessions(subject: string): Promise<SessionInfo[]> {
    const response = await fetch(`${this.chatBaseUrl}/sessions/student/${encodeURIComponent(subject)}`, {
      headers: this.authHeaders(),
    });
    if (!response.ok) {
      throw new Error(`Failed to list sessions: ${response.statusText}`);
    }
    const data = await response.json() as Array<Record<string, unknown>>;
    return (Array.isArray(data) ? data : []).map((row) => ({
      ...(row as unknown as SessionInfo),
      createdAt: new Date((row.createdAt ?? row.startedAt ?? 0) as string),
    }));
  }

  /**
   * Get conversation history
   */
  async getHistory(sessionId: string): Promise<ChatMessage[]> {
    const response = await fetch(`${this.chatBaseUrl}/sessions/${sessionId}/history`, {
      headers: this.authHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get history: ${response.statusText}`);
    }

    const data = await response.json() as {
      messages: Array<{
        id?: string;
        role: ChatMessage['role'];
        content: string;
        timestamp?: string;
        createdAt?: string;
        metadata?: MessageMetadata;
        /** tutor-agent history rows carry these at the top level (teaching-agent: absent). */
        messageId?: string;
        stopped?: boolean;
      }>;
    };
    return data.messages.map((msg, index) => {
      const serverId = msg.id ?? (msg.role === 'assistant' ? msg.messageId : undefined);
      const metadata: MessageMetadata | undefined =
        (serverId && msg.role === 'assistant') || msg.stopped
          ? {
              ...msg.metadata,
              ...(serverId && msg.role === 'assistant'
                ? { messageId: msg.metadata?.messageId ?? serverId }
                : {}),
              ...(msg.stopped ? { stopped: true } : {}),
            }
          : msg.metadata;
      return {
        id: serverId ?? `${sessionId}-history-${index}`,
        role: msg.role,
        content: msg.content,
        timestamp: new Date(msg.timestamp ?? msg.createdAt ?? 0),
        metadata,
      };
    });
  }

  /**
   * End a session
   */
  async endSession(sessionId: string): Promise<void> {
    await fetch(`${this.chatBaseUrl}/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: this.authHeaders(),
    });
  }

  /**
   * Get content recommendations
   */
  async getRecommendations(options: {
    currentTopic: string;
    courseId: string;
    studentLevel?: 'beginner' | 'intermediate' | 'advanced';
    strugglingWith?: string[];
    preferredFormats?: string[];
  }): Promise<ContentRecommendation[]> {
    const response = await fetch(`${this.contentBaseUrl}/content/recommend`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(options),
    });

    if (!response.ok) {
      throw new Error(`Failed to get recommendations: ${response.statusText}`);
    }

    const data = await response.json();
    return data.recommendations;
  }

  /**
   * Search content
   */
  async searchContent(query: string, options: {
    courseId: string;
    types?: string[];
    limit?: number;
  }): Promise<ContentRecommendation[]> {
    const params = new URLSearchParams({ q: query });
    if (options?.types) params.set('types', options.types.join(','));
    params.set('courseId', options.courseId);
    if (options?.limit) params.set('limit', options.limit.toString());

    const response = await fetch(`${this.contentBaseUrl}/content/search?${params}`, {
      headers: this.authHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to search content: ${response.statusText}`);
    }

    const data = await response.json();
    return data.results;
  }

  /**
   * Get video URL
   */
  async getVideoUrl(videoId: string, startTime?: number): Promise<string> {
    const params = new URLSearchParams();
    if (startTime) params.set('t', startTime.toString());

    const response = await fetch(
      `${this.contentBaseUrl}/content/videos/${videoId}/url?${params}`,
      { headers: this.authHeaders() }
    );

    if (!response.ok) {
      throw new Error(`Failed to get video URL: ${response.statusText}`);
    }

    const data = await response.json();
    return data.url;
  }

  /** Timed transcript for a video (for transcript-beside + click-to-seek). */
  async getTranscript(videoId: string, lang = 'en'): Promise<TranscriptSegment[]> {
    const response = await fetch(
      `${this.contentBaseUrl}/content/videos/${videoId}/transcript?lang=${encodeURIComponent(lang)}`,
      { headers: this.authHeaders() }
    );
    if (response.status === 404) return [];
    if (!response.ok) throw new Error(`Failed to get transcript: ${response.statusText}`);
    const data = await response.json();
    return data.segments ?? [];
  }

  /** Toggleable WebVTT caption tracks for a video. */
  async getCaptions(videoId: string): Promise<CaptionTrack[]> {
    const response = await fetch(`${this.contentBaseUrl}/content/videos/${videoId}/captions`, {
      headers: this.authHeaders(),
    });
    if (!response.ok) return [];
    const data = await response.json();
    return (data.captions ?? []).map((c: any) => ({
      lang: c.lang,
      vttUrl: c.vttUrl ?? c.vtt_url,
      source: c.source,
      isDefault: c.isDefault ?? c.is_default ?? false,
    }));
  }

  /** Resume info: the user's saved progress for a video. */
  async getVideoProgress(videoId: string): Promise<VideoProgress | null> {
    const response = await fetch(`${this.contentBaseUrl}/content/videos/${videoId}/progress`, {
      headers: this.authHeaders(),
    });
    if (!response.ok) return null;
    return response.json();
  }

  /**
   * Report watched [start,end] second-intervals + last position. The server
   * merges them, recomputes coverage, and latches completion at the threshold.
   */
  async postVideoProgress(
    videoId: string,
    segments: Array<[number, number]>,
    lastPositionSec?: number
  ): Promise<VideoProgress | null> {
    const response = await fetch(`${this.contentBaseUrl}/content/videos/${videoId}/progress`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ segments, lastPositionSec }),
    });
    if (!response.ok) return null;
    return response.json();
  }

  /**
   * Get book excerpt
   */
  async getBookExcerpt(
    bookId: string,
    chapterId: string,
    options?: {
      around?: string;
      maxLength?: number;
    }
  ): Promise<BookContent> {
    const params = new URLSearchParams();
    if (options?.around) params.set('around', options.around);
    if (options?.maxLength) params.set('maxLength', options.maxLength.toString());

    const response = await fetch(
      `${this.contentBaseUrl}/content/books/${bookId}/chapters/${chapterId}/excerpt?${params}`,
      { headers: this.authHeaders() },
    );

    if (!response.ok) {
      throw new Error(`Failed to get excerpt: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get available skills
   */
  async getSkills(): Promise<{
    available: string[];
    active: string[];
  }> {
    const response = await fetch(`${this.chatBaseUrl}/chat/skills`, {
      headers: this.authHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to get skills: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Activate a skill
   */
  async activateSkill(skillName: string): Promise<void> {
    const response = await fetch(`${this.chatBaseUrl}/chat/skills/activate`, {
      method: 'POST',
      headers: this.authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ skillName }),
    });

    if (!response.ok) {
      throw new Error(`Failed to activate skill: ${response.statusText}`);
    }
  }

  /** True only when chat goes through the /ako BFF; otherwise the memory control stays hidden. */
  isMemoryEnabled(): boolean {
    return this.memoryEnabled;
  }

  private async memoryCall(method: string, path: string, body?: unknown): Promise<Response> {
    if (!this.memoryEnabled) throw new MemoryError('unavailable');
    let response: Response;
    try {
      response = await fetch(`${this.chatBaseUrl}/memory${path}`, {
        method,
        headers: this.authHeaders(body !== undefined ? { 'Content-Type': 'application/json' } : undefined),
        ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      });
    } catch {
      throw new MemoryError('failed');
    }
    if (response.ok) return response;
    let code = '';
    try { code = ((await response.json()) as { error?: string }).error ?? ''; } catch { /* no body */ }
    if (response.status === 404 && code === 'memory_unavailable') throw new MemoryError('unavailable');
    if (response.status === 400) throw new MemoryError('rejected');
    throw new MemoryError('failed');
  }

  /** What Ako remembers about the signed-in learner, newest first. */
  async getMemory(): Promise<MemoryItem[]> {
    const data = (await (await this.memoryCall('GET', '')).json()) as { items?: MemoryItem[] };
    const items = Array.isArray(data.items) ? data.items : [];
    return [...items].sort((a, b) => Date.parse(b.updatedAt ?? '') - Date.parse(a.updatedAt ?? '') || b.id - a.id);
  }

  /** Tell Ako something to remember. The note is trimmed and capped before it is sent. */
  async addMemoryNote(note: string): Promise<MemoryItem> {
    const text = note.trim().slice(0, MEMORY_NOTE_MAX);
    if (!text) throw new MemoryError('rejected');
    return (await (await this.memoryCall('POST', '/preferences', { note: text })).json()) as MemoryItem;
  }

  async deleteMemoryItem(id: number): Promise<void> {
    await this.memoryCall('DELETE', `/${encodeURIComponent(String(id))}`);
  }

  async deleteAllMemory(): Promise<void> {
    await this.memoryCall('DELETE', '');
  }
}

// Export singleton instance
export const teachingAgentAPI = new TeachingAgentAPI();

export default teachingAgentAPI;
